import { useState, useRef, useEffect } from "react";
import { FaEdit, FaPlus } from "react-icons/fa";
import { MdPhoto } from "react-icons/md";
import PhotoEditorModal from "../../components/Profile/PhotoEditorModal";
import EditProfile from "../../components/Profile/EditProfile";
import { getMediaUrl, getProfileImage } from "../../utils/helpers";
import {
    GetMyProfileDetails, GetMyProfilePosts, GetPostSignedUrl,
    uploadFileToSignedUrl, UpdateProfileDetails, DeletePost
} from "../../services/api";
import PhotoViewer from "../../components/Profile/PhotoViewer";
import { MyStory } from "./MyStory";
import MyFollower from "../../components/Profile/MyFollower";
import MyFollowing from "../../components/Profile/MyFollowing";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "../../features/auth/authSlice";
import { showError } from "../../utils/toast";

export default function Profile() {

    const [selectedFile, setSelectedFile] = useState(null);
    const [openEditor, setOpenEditor] = useState(false);
    const [openEditProfile, setOpenEditProfile] = useState(false);
    const [openFollower, setOpenFollower] = useState(false);
    const [openFollowing, setOpenFollowing] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImages, setViewerImages] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [showPostDelete, setShowPostDelete] = useState(false);

    const [profileUrl, setProfileUrl] = useState("");
    const [userName, setUserName] = useState("");
    const [age, setAge] = useState("");
    const [status, setStatus] = useState("");
    const [country, setCountry] = useState("");
    const [countryFlag, setCountryFlag] = useState("");
    const [bio, setBio] = useState("");
    const [postCount, setPostCount] = useState(0);
    const [followerCount, setFollowerCount] = useState(0);
    const [followingCount, setFollowingCount] = useState(0);

    const [postData, setPostData] = useState("");

    const dispatch = useDispatch();

    const loading = useSelector((state) => state?.auth?.loading);
    const isMultipleSession = useSelector((state) => state?.auth?.isMultipleSession);
    const isAccountBlocked = useSelector((state) => state?.auth?.isAccountBlocked);
    const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);

    useEffect(() => {
        fetchPosts();
        fetchDetails();
    }, []);

    const fetchDetails = () => {
        GetMyProfileDetails()
            .then((res) => {
                setProfileUrl(getMediaUrl(res?.data?.ppImageURL));
                setUserName(res?.data?.name);
                setAge(res?.data?.age);
                setStatus("online");
                setCountry(res?.data?.countryId?.name);
                setCountryFlag(res?.data?.countryId?.flag);
                setBio(res?.data?.about);
                setFollowerCount(res?.data?.followerCount);
                setFollowingCount(res?.data?.followingCount);

                dispatch(updateUser({
                    ppImageURL: res?.data?.ppImageURL,
                }));
            })
            .catch((err) => {
                console.log(err)
            });
    }

    const fetchPosts = () => {
        GetMyProfilePosts()
            .then((res) => {
                setPostData(res?.data);
                setPostCount(res?.data?.length);
            })
            .catch((err) => {
                console.log(err)
            });
    }

    const fileInputRef = useRef(null);

    const openFilePicker = () => {
        fileInputRef.current.click();  // trigger hidden input
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setSelectedFile(file);
        setOpenEditor(true);
        e.target.value = "";
    };

    const handleSaveCropped = async (croppedBlob) => {

        const previewUrl = URL.createObjectURL(croppedBlob);

        const file = new File([croppedBlob], "profile.jpg", {
            type: croppedBlob.type,
        });

        setProfileUrl(previewUrl);

        try {
            let imageUrl = file;

            // Upload image ONLY if changed
            if (file) {
                const res = await GetPostSignedUrl();
                const { signedUrl, key, publicUrl } = res.data;

                await uploadFileToSignedUrl(signedUrl, file);

                imageUrl = key;

                dispatch(updateUser({
                    ppImageURL: publicUrl,
                }));
            }

            await UpdateProfileDetails({
                ppImageURL: imageUrl
            });

            fetchPosts();

        } catch (err) {
            console.error(err);
            showError("Something went wrong. Please try again.");
        }
    };

    const profileData = {
        name: userName,
        about: bio,
        age: age,
        ppImageURL: profileUrl,
    };

    const OnUpdateProfile = (updatedProfile) => {

        setProfileUrl(updatedProfile.profileImagePreview);
        setUserName(updatedProfile.name);
        setBio(updatedProfile.about);
        setAge(updatedProfile.age);
        fetchPosts();

        dispatch(updateUser({
            name: updatedProfile.name,
        }));
    };

    const handleDeletePost = async (postId) => {
        try {
            await DeletePost(postId);

            setViewerImages(prev => {
                const updated = prev.filter(img => img.id !== postId);

                if (updated.length === 0) {
                    setViewerOpen(false);
                    return [];
                }

                if (activeIndex >= updated.length) {
                    setActiveIndex(updated.length - 1);
                }

                return updated;
            });

            fetchPosts();
            fetchDetails();
        } catch (err) {
            console.error("Delete post failed", err);
            showError("Failed to delete post");
        }
    };

    const OnFollowerClick = () => {
        setOpenFollower(true)
    }

    const OnFollowingClick = () => {
        setOpenFollowing(true)
    }

    return (
        <div className="min-h-screen bg-black text-white px-4 md:px-16 pt-24 relative">

            {/* ✅ PROFILE SECTION */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8">

                {/* PROFILE IMAGE */}
                <div className="relative">
                    <div className="w-40 h-40 rounded-full bg-gradient-to-tr from-red-500 to-red-700 p-1 cursor-pointer">
                        <img
                            src={getProfileImage(profileUrl)}
                            alt="profile"
                            className="w-full h-full rounded-full object-cover"
                            onClick={() => {
                                setActiveIndex(0);
                                setViewerOpen(true);
                                setShowPostDelete(false);
                                setViewerImages([
                                    {
                                        id: 12345,
                                        url: getProfileImage(profileUrl)
                                    }
                                ]);
                            }}
                        />
                    </div>
                </div>

                {/* PROFILE DETAILS */}
                <div className="flex-1 w-full">
                    <h1 className="text-2xl md:text-3xl font-bold">
                        {userName}
                    </h1>

                    <div className="flex items-center gap-3 mt-2 text-sm text-gray-400">
                        {country}
                        <img
                            src={countryFlag}
                            alt="flag"
                            className="w-6 h-4 rounded-sm"
                        />
                        <span
                            className={`flex items-center gap-1 ${status === "online" ? "text-green-400" : "text-gray-400"}`}
                        >
                            <span
                                className={`w-2 h-2 rounded-full ${status === "online" ? "bg-green-400" : "bg-gray-400"}`}
                            ></span>

                            {status === "online" ? "Available" : "Offline"}
                        </span>
                    </div>

                    <div className="flex items-center gap-3 mt-2 text-sm text-gray-400">
                        {bio}
                    </div>

                    {/* STATS */}
                    <div className="flex gap-10 mt-6">
                        <div className="text-center">
                            <h2 className="text-xl font-bold">{postCount}</h2>
                            <p className="text-sm text-gray-400">Post</p>
                        </div>
                        <div onClick={OnFollowerClick} className="text-center cursor-pointer">
                            <h2 className="text-xl font-bold">{followerCount}</h2>
                            <p className="text-sm text-gray-400">Follower</p>
                        </div>
                        <div onClick={OnFollowingClick} className="text-center cursor-pointer">
                            <h2 className="text-xl font-bold">{followingCount}</h2>
                            <p className="text-sm text-gray-400">Following</p>
                        </div>
                    </div>

                    {/* BUTTONS */}
                    <div className="flex flex-wrap gap-4 mt-6">
                        <button
                            onClick={() => setOpenEditProfile(true)}
                            className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 transition px-6 py-3 rounded-full">
                            <FaEdit />
                            Edit Profile
                        </button>
                    </div>
                </div>
            </div>

            <MyStory />

            <div className="flex flex-wrap gap-2 items-center text-lg font-bold mt-10">
                <MdPhoto size={30} />
                Posts {postData.length}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 py-10">

                <div>
                    {/* CLICKABLE BOX */}
                    <div
                        onClick={openFilePicker}
                        className="aspect-square bg-[#4b4f57] flex items-center justify-center rounded-lg cursor-pointer"
                    >
                        <FaPlus className="text-white text-3xl opacity-60" />
                    </div>

                    <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={handleImageSelect}
                        className="hidden"
                    />

                    {openEditor && (
                        <PhotoEditorModal
                            file={selectedFile}
                            onClose={() => setOpenEditor(false)}
                            onSave={handleSaveCropped}
                        />
                    )}
                </div>

                {postData && postData?.map((posts, index) => (
                    <div
                        key={posts._id}
                        className="relative bg-gray-900 cursor-pointer aspect-square overflow-hidden"
                        onClick={() => {
                            setActiveIndex(index);
                            setViewerOpen(true);
                            setShowPostDelete(true);
                            setViewerImages(
                                postData?.map(post => ({
                                    id: post._id,
                                    url: `${process.env.REACT_APP_MEDIA_BASE_URL}${post.url}`
                                })) || []
                            );
                        }}
                    >
                        <img
                            src={`${process.env.REACT_APP_MEDIA_BASE_URL}${posts.url}`}
                            alt="posts"
                            className="w-full h-full object-cover"
                        />
                    </div>
                ))}

                {/* Viewer */}
                {viewerOpen && (
                    <PhotoViewer
                        images={viewerImages}
                        currentIndex={activeIndex}
                        onClose={() => setViewerOpen(false)}
                        onPrev={() => setActiveIndex((i) => i - 1)}
                        onNext={() => setActiveIndex((i) => i + 1)}
                        showMenuOption={showPostDelete}
                        onDeleteImage={handleDeletePost}
                    />
                )}
            </div>

            {openEditProfile && (
                <EditProfile
                    user={profileData}
                    onClose={() => setOpenEditProfile(false)}
                    onUpdatedProfile={(updatedProfile) => {
                        OnUpdateProfile(updatedProfile)
                    }}
                />
            )}

            {openFollower && (
                <MyFollower
                    onClose={() => setOpenFollower(false)}
                />
            )}

            {openFollowing && (
                <MyFollowing
                    onClose={() => setOpenFollowing(false)}
                />
            )}

        </div>
    );
}